import React, { useState, useEffect } from 'react';
import { Mail, Send, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import EmailPreviewModal from './EmailPreviewModal';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * EmailOutreachPanel - Create email drafts or send emails to selected candidates
 *
 * Props:
 * - rfqId: UUID of the RFQ
 * - selectedCandidateIds: Array of candidate UUIDs
 * - onEmailsSent: Callback when emails are created/sent
 */
const EmailOutreachPanel = ({ rfqId, selectedCandidateIds = [], onEmailsSent }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [mode, setMode] = useState('draft'); // 'draft' or 'send'
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Auto-select default template or first template
    if (templates.length > 0 && !selectedTemplateId) {
      const defaultTemplate = templates.find(t => t.is_default);
      setSelectedTemplateId(defaultTemplate?.id || templates[0].id);
    }
  }, [templates]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/matching/email-templates?type=candidate_individual_outreach`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTemplates(data.templates || []);
      } else {
        throw new Error(data.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async () => {
    if (selectedCandidateIds.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    if (!selectedTemplateId) {
      setError('Please select an email template');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_URL}/matching/rfq/${rfqId}/outreach`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidateIds: selectedCandidateIds,
          templateId: selectedTemplateId,
          mode
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Backend (createEmailDrafts) returns { success, created, failed, jobs, errors }
        const createdCount = data.created ?? data.jobs?.length ?? 0;
        const failedCount = data.failed ?? 0;

        let message = mode === 'draft'
          ? `${createdCount} email draft${createdCount !== 1 ? 's' : ''} created in Gmail`
          : `${createdCount} email${createdCount !== 1 ? 's' : ''} sent`;

        if (failedCount > 0) {
          message += ` — ${failedCount} failed`;
        }

        setSuccess(message);
        if (onEmailsSent) onEmailsSent(data.jobs);
      } else {
        throw new Error(data.message || 'Failed to create emails');
      }
    } catch (err) {
      console.error('Error sending emails:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            Email Outreach
          </CardTitle>
          <CardDescription>
            {selectedCandidateIds.length > 0
              ? `Send emails to ${selectedCandidateIds.length} selected candidate${selectedCandidateIds.length !== 1 ? 's' : ''}`
              : 'Select candidates to send outreach emails'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {/* Template Selection */}
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              Email Template
            </label>
            {templates.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No email templates found. Create a template in settings first.
                </AlertDescription>
              </Alert>
            ) : (
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-background"
              >
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {template.is_default && ' (Default)'}
                  </option>
                ))}
              </select>
            )}
            {selectedTemplate && (
              <div className="mt-2 p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">Subject:</p>
                <p className="text-sm text-muted-foreground">{selectedTemplate.subject_template}</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setPreviewOpen(true)}
                  className="p-0 h-auto mt-2"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Preview template
                </Button>
              </div>
            )}
          </div>

          {/* Mode Selection */}
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-2">
              Delivery Mode
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="draft"
                  checked={mode === 'draft'}
                  onChange={(e) => setMode(e.target.value)}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">Create Drafts (Gmail)</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value="send"
                  checked={mode === 'send'}
                  onChange={(e) => setMode(e.target.value)}
                  className="rounded-full border-gray-300"
                />
                <span className="text-sm">Send Immediately</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {mode === 'draft'
                ? 'Email drafts will be created in your Gmail account for review before sending'
                : 'Emails will be sent immediately via Gmail API'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              {selectedCandidateIds.length} candidate{selectedCandidateIds.length !== 1 ? 's' : ''} selected
            </div>
            <Button
              onClick={handleSendEmails}
              disabled={sending || selectedCandidateIds.length === 0 || !selectedTemplateId}
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {mode === 'draft' ? 'Creating Drafts...' : 'Sending...'}
                </>
              ) : (
                <>
                  {mode === 'draft' ? (
                    <FileText className="h-4 w-4 mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {mode === 'draft' ? 'Create Drafts' : 'Send Emails'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Preview Modal */}
      {selectedTemplate && (
        <EmailPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          template={selectedTemplate}
          rfqId={rfqId}
          candidateId={selectedCandidateIds[0]} // Preview with first selected candidate
        />
      )}
    </>
  );
};

export default EmailOutreachPanel;
